import { useState } from "react";

interface DragHandlers {
  handleDragStart: (e: React.DragEvent, index: number) => void;
  handleDragOver: (e: React.DragEvent, index: number) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, index: number) => void;
}

interface CellStyles {
  cursor?: string;
  backgroundColor?: string;
  position?: string;
  "&::before"?: {
    content: string;
    position: string;
    top: number;
    left: number;
    width: string;
    height: string;
    backgroundColor: string;
    zIndex: number;
  };
}

export function useColumnDrag({ onDrop }: { onDrop?: (sourceIndex: number, targetIndex: number) => void }): {
  handlers: DragHandlers;
  getCellStyles: (columnIndex: number) => CellStyles;
} {
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);

  const getCellStyles = (columnIndex: number): CellStyles => ({
    cursor: onDrop ? "grab" : "default",
    ...(draggedOverIndex === columnIndex && {
      backgroundColor: "action.hover",
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "2px",
        height: "100vh",
        backgroundColor: "other.navigationMain",
        zIndex: 1
      }
    })
  });

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDraggedOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDraggedOverIndex(null);
    if (onDrop) {
      const sourceIndex = Number.parseInt(e.dataTransfer.getData("text/plain"));
      onDrop(sourceIndex, index);
    }
  };

  return {
    handlers: {
      handleDragStart,
      handleDragOver,
      handleDragLeave,
      handleDrop
    },
    getCellStyles
  };
}
